package mapsearch.globalsearch

import apiclient.FormationClient
import apiclient.geocoder.GeocodedLocation
import apiclient.geocoder.geocode
import apiclient.mapsearch.SearchSpec
import dev.fritz2.core.RootStore
import koin.withKoin
import kotlin.time.Duration
import kotlin.time.Duration.Companion.seconds
import kotlin.time.measureTimedValue
import kotlinx.coroutines.Job
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import mu.KotlinLogging

sealed interface GeocodingSearchState {
    object NotActive : GeocodingSearchState
    object Fetching : GeocodingSearchState
    data class Results(val results: List<GeocodedLocation>, val duration: Duration, val spec: SearchSpec) : GeocodingSearchState
}

private val logger = KotlinLogging.logger("GeocodingResultsStore")

class GeocodingResultsStore : RootStore<GeocodingSearchState>(GeocodingSearchState.NotActive, Job()) {
    private var geocodingJob: Job? = null

    private var lastGeocodingQuery: String? = null
    private var lastGeocodingTime: Instant? = null
    private val debounce: Duration = 2.seconds
    private var debounceJob: Job? = null

    private fun debounceExceeded() = lastGeocodingTime?.let { lastSearch ->
        Clock.System.now() > lastSearch + debounce
    } ?: true

    fun geocode(searchSpec: SearchSpec) {
        if (searchSpec.query.isNullOrBlank()) {
            update(GeocodingSearchState.NotActive)
        } else if (searchSpec.query != lastGeocodingQuery && debounceExceeded()) {
            lastGeocodingQuery = searchSpec.query
            lastGeocodingTime = Clock.System.now()
            killDebounceJob()
            geocodingJob = MainScope().launch {
                update(GeocodingSearchState.Fetching)
                withKoin {
                    val client = get<FormationClient>()
                    val results = measureTimedValue {
                        client.geocode(searchSpec.near ?: error("need location to geocode"), searchSpec.query ?: error("need query to geocode")).getOrThrow()
                    }
                    console.info("geocoded ${searchSpec.query} (${searchSpec.near})", results)
                    update(GeocodingSearchState.Results(results.value, results.duration, searchSpec))
                }
            }
        } else {
            when {
                (searchSpec.query == lastGeocodingQuery) -> {
                    logger.info { "Geocoding skipped -> same query" }
                }

                else -> {
                    logger.info { "Geocoding skipped -> debounce time not reached" }
                    restartDebounceJob(searchSpec)
                }
            }
        }
    }

    fun reset() {
        runCatching {
            geocodingJob?.let {
                if (it.isActive) {
                    it.cancel("geocoding cancelled")
                }
            }
        }

        update(GeocodingSearchState.NotActive)
    }

    private fun killDebounceJob() {
        debounceJob?.let {
            if (it.isActive) {
                it.cancel("cancel debounce")
                logger.info { "Geocoding debounce job cancelled" }
            }
        }
        debounceJob = null
    }

    private fun startDebounceJob(searchSpec: SearchSpec) {
        debounceJob = MainScope().launch {
            logger.info { "Geocoding debounce job restarted" }
            delay(debounce)
            logger.info { "Geocoding debounce time reached -> execute Geocoding with last query" }
            geocode(searchSpec)
        }
    }

    private fun restartDebounceJob(searchSpec: SearchSpec) {
        killDebounceJob()
        startDebounceJob(searchSpec)
    }
}
